<script setup lang="ts">
import { computed, ref } from "vue";
import ColorPicker from "../ColorPicker.vue";
import MergeTagInput from "../MergeTagInput.vue";
import CollapsibleSection from "./CollapsibleSection.vue";
import SpacingControl from "../SpacingControl.vue";
import { useI18n } from "../../composables/useI18n";
import {
  inputClass,
  inputGroupInputClass,
  inputSuffixClass,
  labelClass,
} from "../../constants/styleConstants";
import type {
  ButtonBorderSides,
  InputBlock,
  InputFieldType,
  SpacingValue,
} from "@aswin.dev/types";

const props = defineProps<{
  block: InputBlock;
  fontFamilies: Array<{ value: string; label: string }>;
}>();

const emit = defineEmits<{
  (e: "update", updates: Partial<InputBlock>): void;
}>();

const { t } = useI18n();

const openLabelSection = ref(true);
const openInputSection = ref(true);

function updateField<K extends keyof InputBlock>(
  field: K,
  value: InputBlock[K],
): void {
  emit("update", { [field]: value } as Partial<InputBlock>);
}

const BORDER_SIDE_IDS: ButtonBorderSides[] = [
  "all",
  "top",
  "right",
  "bottom",
  "left",
];

const borderSideOptions = BORDER_SIDE_IDS.map((id) => ({
  id,
  label:
    t.button.borderSidesLabels[id as keyof typeof t.button.borderSidesLabels],
}));

const inputTypes: { value: InputFieldType; label: string }[] = [
  { value: "text", label: t.input.types.text },
  { value: "email", label: t.input.types.email },
  { value: "date", label: t.input.types.date },
];

function patchSpacing<
  K extends "labelMargin" | "labelPadding" | "inputMargin" | "inputPadding",
>(key: K, value: SpacingValue): void {
  emit("update", { [key]: value } as Partial<InputBlock>);
}

const widthMode = computed(() =>
  props.block.inputWidth === "full" ? "full" : "fixed",
);

function setWidthMode(ev: Event): void {
  const v = (ev.target as HTMLSelectElement).value;
  if (v === "full") {
    updateField("inputWidth", "full");
  } else {
    const n =
      typeof props.block.inputWidth === "number" ? props.block.inputWidth : 280;
    updateField("inputWidth", n);
  }
}

function toggleLabelSection(): void {
  openLabelSection.value = !openLabelSection.value;
}

function toggleInputSection(): void {
  openInputSection.value = !openInputSection.value;
}
</script>

<template>
  <div class="tpl:mb-3.5">
    <label :class="labelClass">{{ t.input.fieldType }}</label>
    <select
      :class="inputClass"
      :value="block.inputType"
      @change="
        updateField(
          'inputType',
          ($event.target as HTMLSelectElement).value as InputFieldType,
        )
      "
    >
      <option v-for="opt in inputTypes" :key="opt.value" :value="opt.value">
        {{ opt.label }}
      </option>
    </select>
  </div>

  <div class="tpl:mb-3.5">
    <label :class="labelClass">{{ t.input.name }}</label>
    <input
      type="text"
      :class="inputClass"
      :value="block.name"
      autocomplete="off"
      spellcheck="false"
      @input="
        updateField('name', ($event.target as HTMLInputElement).value.trim())
      "
    />
  </div>

  <div class="tpl:mb-3.5">
    <label :class="labelClass">{{ t.input.label }}</label>
    <MergeTagInput
      :model-value="block.label"
      type="text"
      @update:model-value="updateField('label', $event)"
    />
  </div>

  <div class="tpl:mb-3.5">
    <label :class="labelClass">{{ t.input.placeholder }}</label>
    <MergeTagInput
      :model-value="block.placeholder"
      type="text"
      @update:model-value="updateField('placeholder', $event)"
    />
  </div>

  <div class="tpl:mb-3.5">
    <label :class="labelClass">{{ t.input.defaultValue }}</label>
    <MergeTagInput
      :model-value="block.defaultValue ?? ''"
      type="text"
      @update:model-value="updateField('defaultValue', $event)"
    />
  </div>

  <label
    class="tpl:mb-3.5 tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-[13px] tpl:text-[var(--tpl-text)]"
  >
    <input
      type="checkbox"
      class="tpl:size-3.5 tpl:cursor-pointer tpl:accent-[var(--tpl-primary)]"
      :checked="block.required ?? false"
      @change="
        updateField('required', ($event.target as HTMLInputElement).checked)
      "
    />
    {{ t.input.required }}
  </label>

  <CollapsibleSection
    :title="t.input.labelAppearance"
    :open="openLabelSection"
    no-border
    @toggle="toggleLabelSection"
  >
    <SpacingControl
      :model-value="block.labelMargin"
      :label="t.input.labelMargin"
      @update:model-value="patchSpacing('labelMargin', $event)"
    />
    <SpacingControl
      :model-value="block.labelPadding"
      :label="t.input.labelPadding"
      @update:model-value="patchSpacing('labelPadding', $event)"
    />
    <div class="tpl:mb-3.5">
      <label :class="labelClass">{{ t.input.fontFamily }}</label>
      <select
        :class="inputClass"
        :value="block.labelFontFamily || ''"
        @change="
          updateField(
            'labelFontFamily',
            ($event.target as HTMLSelectElement).value || undefined,
          )
        "
      >
        <option value="">{{ t.input.inheritFont }}</option>
        <option
          v-for="font in fontFamilies"
          :key="font.value"
          :value="font.value"
        >
          {{ font.label }}
        </option>
      </select>
    </div>
    <div class="tpl:mb-3.5">
      <label :class="labelClass">{{ t.button.fontSize }}</label>
      <div class="tpl:flex tpl:items-stretch">
        <input
          type="number"
          :class="inputGroupInputClass"
          :value="block.labelFontSize"
          min="8"
          max="48"
          @input="
            updateField(
              'labelFontSize',
              Number(($event.target as HTMLInputElement).value) || 14,
            )
          "
        />
        <span :class="inputSuffixClass">px</span>
      </div>
    </div>
    <div class="tpl:mb-3.5">
      <label :class="labelClass">{{ t.button.textColor }}</label>
      <ColorPicker
        :model-value="block.labelColor"
        @update:model-value="updateField('labelColor', $event)"
      />
    </div>
    <div class="tpl:mb-3.5">
      <label :class="labelClass">{{ t.input.fontWeight }}</label>
      <select
        :class="inputClass"
        :value="block.labelFontWeight"
        @change="
          updateField(
            'labelFontWeight',
            ($event.target as HTMLSelectElement).value as 'normal' | 'bold',
          )
        "
      >
        <option value="normal">{{ t.input.weightNormal }}</option>
        <option value="bold">{{ t.input.weightBold }}</option>
      </select>
    </div>
    <div class="tpl:mb-3.5">
      <label :class="labelClass">{{ t.menu.textAlign }}</label>
      <select
        :class="inputClass"
        :value="block.labelTextAlign"
        @change="
          updateField(
            'labelTextAlign',
            ($event.target as HTMLSelectElement).value as
              | 'left'
              | 'center'
              | 'right',
          )
        "
      >
        <option value="left">{{ t.input.alignLeft }}</option>
        <option value="center">{{ t.input.alignCenter }}</option>
        <option value="right">{{ t.input.alignRight }}</option>
      </select>
    </div>
  </CollapsibleSection>

  <CollapsibleSection
    :title="t.input.inputAppearance"
    :open="openInputSection"
    @toggle="toggleInputSection"
  >
    <SpacingControl
      :model-value="block.inputMargin"
      :label="t.input.inputMargin"
      @update:model-value="patchSpacing('inputMargin', $event)"
    />
    <SpacingControl
      :model-value="block.inputPadding"
      :label="t.input.inputPadding"
      @update:model-value="patchSpacing('inputPadding', $event)"
    />
    <div class="tpl:mb-3.5">
      <label :class="labelClass">{{ t.input.fieldWidth }}</label>
      <select :class="inputClass" :value="widthMode" @change="setWidthMode">
        <option value="full">{{ t.video.fullWidth }}</option>
        <option value="fixed">{{ t.input.fixedWidth }}</option>
      </select>
    </div>
    <div v-if="block.inputWidth !== 'full'" class="tpl:mb-3.5">
      <label :class="labelClass">{{ t.video.width }}</label>
      <div class="tpl:flex tpl:items-stretch">
        <input
          type="number"
          :class="inputGroupInputClass"
          :value="typeof block.inputWidth === 'number' ? block.inputWidth : 280"
          min="80"
          max="1200"
          @input="
            updateField(
              'inputWidth',
              Math.max(
                80,
                Number(($event.target as HTMLInputElement).value) || 280,
              ),
            )
          "
        />
        <span :class="inputSuffixClass">px</span>
      </div>
    </div>
    <div class="tpl:mb-3.5">
      <label :class="labelClass">{{ t.input.fontFamily }}</label>
      <select
        :class="inputClass"
        :value="block.inputFontFamily || ''"
        @change="
          updateField(
            'inputFontFamily',
            ($event.target as HTMLSelectElement).value || undefined,
          )
        "
      >
        <option value="">{{ t.input.inheritFont }}</option>
        <option
          v-for="font in fontFamilies"
          :key="font.value"
          :value="font.value"
        >
          {{ font.label }}
        </option>
      </select>
    </div>
    <div class="tpl:mb-3.5 tpl:flex tpl:flex-col tpl:gap-3">
      <div>
        <label :class="labelClass">{{ t.button.background }}</label>
        <ColorPicker
          :model-value="block.inputBackgroundColor"
          @update:model-value="updateField('inputBackgroundColor', $event)"
        />
      </div>
      <div>
        <label :class="labelClass">{{ t.button.textColor }}</label>
        <ColorPicker
          :model-value="block.inputTextColor"
          @update:model-value="updateField('inputTextColor', $event)"
        />
      </div>
      <div>
        <label :class="labelClass">{{ t.input.placeholderColor }}</label>
        <ColorPicker
          :model-value="block.inputPlaceholderColor"
          @update:model-value="updateField('inputPlaceholderColor', $event)"
        />
      </div>
    </div>
    <div class="tpl:mb-3.5">
      <label :class="labelClass">{{ t.button.fontSize }}</label>
      <div class="tpl:flex tpl:items-stretch">
        <input
          type="number"
          :class="inputGroupInputClass"
          :value="block.inputFontSize"
          min="10"
          max="36"
          @input="
            updateField(
              'inputFontSize',
              Number(($event.target as HTMLInputElement).value) || 15,
            )
          "
        />
        <span :class="inputSuffixClass">px</span>
      </div>
    </div>
    <div class="tpl:mb-3.5">
      <label :class="labelClass">{{ t.button.borderRadius }}</label>
      <div class="tpl:flex tpl:items-stretch">
        <input
          type="number"
          :class="inputGroupInputClass"
          :value="block.inputBorderRadius"
          min="0"
          max="40"
          @input="
            updateField(
              'inputBorderRadius',
              Number(($event.target as HTMLInputElement).value) || 0,
            )
          "
        />
        <span :class="inputSuffixClass">px</span>
      </div>
    </div>
    <div class="tpl:mb-3.5">
      <label :class="labelClass">{{ t.button.border }}</label>
      <div class="tpl:flex tpl:flex-col tpl:gap-3">
        <div>
          <label :class="labelClass">{{ t.button.borderColor }}</label>
          <ColorPicker
            :model-value="block.inputBorderColor ?? '#d1d5db'"
            @update:model-value="updateField('inputBorderColor', $event)"
          />
        </div>
        <div>
          <label :class="labelClass">{{ t.button.borderWidth }}</label>
          <div class="tpl:flex tpl:items-stretch">
            <input
              type="number"
              :class="inputGroupInputClass"
              :value="block.inputBorderWidth ?? 0"
              min="0"
              max="20"
              @input="
                updateField(
                  'inputBorderWidth',
                  Math.max(
                    0,
                    Number(($event.target as HTMLInputElement).value) || 0,
                  ),
                )
              "
            />
            <span :class="inputSuffixClass">px</span>
          </div>
        </div>
        <div>
          <label :class="labelClass">{{ t.button.borderSides }}</label>
          <select
            :class="inputClass"
            :value="block.inputBorderSides ?? 'all'"
            @change="
              updateField(
                'inputBorderSides',
                ($event.target as HTMLSelectElement).value as ButtonBorderSides,
              )
            "
          >
            <option
              v-for="opt in borderSideOptions"
              :key="opt.id"
              :value="opt.id"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </CollapsibleSection>
</template>
